import { X } from 'lucide-react';
import { useOptionalPodcastPlayer } from '@/contexts/PodcastPlayerContext';
import { PlayPauseButton } from './PodcastControls';
import { formatClock } from './PodcastPill';

/**
 * The lesson's podcast, docked to the bottom of the reading column once its
 * in-text strip is off screen - scrolled past, or on another section - so it
 * can be paused without scrolling back. Sticky inside the reader's own scroll
 * container, so it never covers the app's chrome. `onShow` brings the strip
 * (or the section it belongs to) back.
 */
export function PodcastDock({ onShow }: { onShow: () => void }) {
  const player = useOptionalPodcastPlayer();
  const track = player?.track;
  if (!player || !track) return null;
  const played =
    player.duration > 0 ? Math.min(1, player.currentTime / player.duration) : 0;
  return (
    // Below `lg` the lesson's floating Ask button sits over the reader's
    // foot, so the dock rides above it there.
    <div className='sticky bottom-16 z-10 mx-auto mt-8 max-w-[68ch] lg:bottom-3'>
      <div
        role='group'
        aria-label='Podcast player'
        className='flex items-center gap-3 rounded-full border border-border bg-card py-1.5 pl-1.5 pr-2 shadow-md'
      >
        <PlayPauseButton
          size='sm'
          playing={player.isPlaying}
          onClick={player.toggle}
          label={`podcast of section ${track.sectionIndex}`}
        />
        <button
          type='button'
          onClick={onShow}
          aria-label={`Back to the podcast of section ${track.sectionIndex}: ${track.sectionTitle}`}
          className='tap-target min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          <span className='flex items-baseline gap-2'>
            <span className='shrink-0 font-mono text-xs text-muted-foreground'>
              S{track.sectionIndex}
            </span>
            <span className='truncate text-sm font-medium text-foreground'>
              {track.sectionTitle}
            </span>
          </span>
          <span
            className='mt-1 block h-0.5 overflow-hidden rounded-full bg-muted'
            aria-hidden='true'
          >
            <span
              className='block h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-300'
              style={{ width: `${played * 100}%` }}
            />
          </span>
        </button>
        <span className='shrink-0 font-mono text-xs tabular-nums text-muted-foreground'>
          {formatClock(Math.max(0, player.duration - player.currentTime))}
        </span>
        <button
          type='button'
          onClick={player.close}
          aria-label='Stop and close the podcast'
          className='tap-target shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          <X className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>
    </div>
  );
}
